import type { FC } from 'react';
import { Suspense, lazy } from 'react';
import { Route, Switch } from 'react-router-dom';

import { WhenFeatureIsEnabled } from 'app/configuration/WhenFeatureIsEnabled';
import { ErrorBoundary } from 'components/feedback/ErrorBoundary';
import { ErrorCodes, ErrorPage } from 'components/feedback/state/errors/ErrorPage';
import { Page } from 'components/layout/page/Page';

const WhenUserHasScope = lazy(async () => ({
  default: (await import(/* webpackChunkName: "user-scope" */ 'components/auth/WhenUserHasScope')).WhenUserHasScope
}));
const LogoutPage = lazy(async () => ({
  default: (await import(/* webpackChunkName: "logout-page" */ 'pages/authentication/LogoutPage')).LogoutPage
}));
const ProjectsPage = lazy(async () => ({
  default: (await import(/* webpackChunkName: "projects-page" */ 'pages/projects/ProjectsPage')).ProjectsPage
}));
const TemplatesPage = lazy(async () => ({
  default: (await import(/* webpackChunkName: "templates-page" */ 'pages/templates/TemplatesPage')).TemplatesPage
}));
const LibrariesPage = lazy(async () => ({
  default: (await import(/* webpackChunkName: "libraries-page" */ 'pages/libraries/LibrariesRoutes')).LibrariesPage
}));
const IntegrationsPage = lazy(async () => ({
  default: (await import(/* webpackChunkName: "integrations-page" */ 'pages/integrations/IntegrationsPage'))
    .IntegrationsPage
}));
const PersonalSettingsPage = lazy(async () => ({
  default: (
    await import(/* webpackChunkName: "personal-settings-page" */ 'pages/personalSettings/PersonalSettingsPage')
  ).PersonalSettingsPage
}));
const SubscriptionSettingsPage = lazy(async () => ({
  default: (await import(/* webpackChunkName: "subscriptions-page" */ 'pages/subscriptions/SubscriptionSettingsPage'))
    .SubscriptionSettingsPage
}));

export const AppRouter: FC = () => {
  return (
    <Switch>
      <Route path='/logout'>
        <ErrorBoundary>
          <Suspense fallback={<Page loading />}>
            <LogoutPage />
          </Suspense>
        </ErrorBoundary>
      </Route>

      <Route path='/templates'>
        <ErrorBoundary>
          <Suspense fallback={<Page loading />}>
            <WhenUserHasScope scope='template:creator' noAccess={<ErrorPage code={ErrorCodes.Unauthorized} />}>
              <TemplatesPage />
            </WhenUserHasScope>
          </Suspense>
        </ErrorBoundary>
      </Route>

      <Route path='/libraries'>
        <ErrorBoundary>
          <Suspense fallback={<Page loading />}>
            <WhenFeatureIsEnabled featureName='KnowledgeManagement' disabled={<ErrorPage code={ErrorCodes.NotFound} />}>
              <WhenUserHasScope scope='template:creator' noAccess={<ErrorPage code={ErrorCodes.Unauthorized} />}>
                <LibrariesPage />
              </WhenUserHasScope>
            </WhenFeatureIsEnabled>
          </Suspense>
        </ErrorBoundary>
      </Route>

      <Route path='/integrations'>
        <ErrorBoundary>
          <Suspense fallback={<Page loading />}>
            <WhenFeatureIsEnabled featureName='Integrations' disabled={<ErrorPage code={ErrorCodes.NotFound} />}>
              <WhenUserHasScope scope='integrations:owner' noAccess={<ErrorPage code={ErrorCodes.Unauthorized} />}>
                <IntegrationsPage />
              </WhenUserHasScope>
            </WhenFeatureIsEnabled>
          </Suspense>
        </ErrorBoundary>
      </Route>

      <Route path='/subscription'>
        <ErrorBoundary>
          <Suspense fallback={<Page loading />}>
            <WhenUserHasScope scope='subscription:owner' noAccess={<ErrorPage code={ErrorCodes.Unauthorized} />}>
              <SubscriptionSettingsPage />
            </WhenUserHasScope>
          </Suspense>
        </ErrorBoundary>
      </Route>

      <Route path='/personal-settings'>
        <ErrorBoundary>
          <Suspense fallback={<Page loading />}>
            <PersonalSettingsPage />
          </Suspense>
        </ErrorBoundary>
      </Route>

      <Route path={['/projects', '/']}>
        <ErrorBoundary>
          <Suspense fallback={<Page loading />}>
            <ProjectsPage />
          </Suspense>
        </ErrorBoundary>
      </Route>
    </Switch>
  );
};
